import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ArticleDetailQuery, ArticlePushNotificationInput } from "@/gen/graphql";
import {
    articlePushNotificationTargets,
    sendArticlePushNotificationMutation,
} from "@/routes/articles/queries";

type Article = ArticleDetailQuery["article"];
type SendPushNotificationButtonProps = {
    article: Article;
};
export function SendPushNotificationButton({ article }: SendPushNotificationButtonProps) {
    const [isDialogOpen, setDialogOpen] = useState(false);

    const [numTargetsQuery] = useQuery({
        query: articlePushNotificationTargets,
        variables: { id: article.id },
        pause: !isDialogOpen,
    });

    const [_sendResult, sendArticlePushNotification] = useMutation(
        sendArticlePushNotificationMutation
    );

    const handleSubmit = useCallback(
        async (input: ArticlePushNotificationInput) => {
            const result = await sendArticlePushNotification({ id: article.id, input });
            if (result.error) {
                toast.error("Failed to send push notification", {
                    description: result.error.message,
                });
                return;
            }

            toast.success("Push notification sent");
            setDialogOpen(false);
        },
        [article.id, sendArticlePushNotification]
    );

    if (article.pushNotificationSent) {
        return (
            <Button variant="secondary" disabled>
                Push notification sent
            </Button>
        );
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
                <Button variant="secondary">Send push notification ...</Button>
            </DialogTrigger>
            <DialogContent>
                <SendPushNotificationDialogForm
                    article={article}
                    onSubmit={handleSubmit}
                    numTargets={numTargetsQuery.data?.article.totalPushNotificationTargets}
                />
            </DialogContent>
        </Dialog>
    );
}

const zForm = z.object({
    title: z.string().min(3).max(40),
    body: z.string().nonempty(),
});
type FormData = z.infer<typeof zForm>;

const numberFormatter = new Intl.NumberFormat(undefined, {
    style: "decimal",
    maximumFractionDigits: 0,
});

function SendPushNotificationDialogForm({
    article,
    numTargets,
    onSubmit,
}: {
    article: Article;
    numTargets?: number;
    onSubmit: (data: ArticlePushNotificationInput) => void;
}) {
    const form = useForm({
        mode: "onChange",
        defaultValues: {
            title: article.title,
            body: "",
        },
        resolver: zodResolver(zForm),
    });

    const confirmSubmit: SubmitHandler<FormData> = useCallback(
        input => {
            if (confirm(`Ready to send?`)) {
                onSubmit(input);
            }
        },
        [onSubmit]
    );

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(confirmSubmit)} className="flex flex-col gap-8">
                <DialogHeader>
                    <DialogTitle>Send push notification</DialogTitle>
                    <DialogDescription>
                        The push notification is sent to users who have favorited a tagged
                        tournament or a <em>featured</em> tagged player. Users following only{" "}
                        <em>mentioned</em> players will not be notified.
                    </DialogDescription>
                </DialogHeader>
                <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                                <Input placeholder="Title" {...field} />
                            </FormControl>
                            <FormDescription>
                                The bold text on the first line of the push notification. Keep it
                                short!
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="body"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Body</FormLabel>
                            <FormControl>
                                <Input placeholder="Body" {...field} />
                            </FormControl>
                            <FormDescription>
                                The rest of the push notification. Use normal ending punctuation.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <DialogFooter>
                    <Button
                        type="submit"
                        disabled={!form.formState.isValid || form.formState.isSubmitting}
                    >
                        {numTargets !== undefined
                            ? `Send to ${numberFormatter.format(numTargets)} devices`
                            : "Send to devices"}
                    </Button>
                </DialogFooter>
            </form>
        </Form>
    );
}
